import React, { useState, useEffect } from 'react';
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { hsvaToRgbString, hsvaToHex } from "@uiw/react-color";
import tinycolor from "tinycolor2";
import { useWindowSize } from "react-use";
import Wheel from "@uiw/react-color-wheel";
import { BorderedDropDown } from '../../HouseStyle/Components/DropDowns';
import { BorderedTextInput } from '../../HouseStyle/Components/Inputs';
import { ButtonBlue } from '../../HouseStyle/Components/Buttons';

const ColorPickerModal = ({ 
  show, 
  onHide, 
  initialColor = '#ffffff', 
  onSave 
}) => {
  const [color, setColor] = useState({ h: 0, s: 0, v: 100, a: 1 });
  const [colorType, setColorType] = useState("1"); // 1=HEX 2=HSL 3=RGB
  const [colorValue, setColorValue] = useState('#ffffff');
  const [iosDevice, setIosDevice] = useState(false);
  const { width } = useWindowSize();

  useEffect(() => {
    setIosDevice(isIOSDevice());
  }, []);

  // Initialize color when modal opens or initialColor changes
  useEffect(() => {
    if (show && initialColor) {
      const hsvColor = tinycolor(initialColor).toHsv();
      setColor({
        h: hsvColor.h,
        s: hsvColor.s * 100,
        v: hsvColor.v * 100,
        a: hsvColor.a,
      });
      setColorValue(initialColor);
    }
  }, [show, initialColor]);

  // Update colorValue when colorType or color changes
  useEffect(() => {
    let newColorValue;
    if (colorType === "1") {
      newColorValue = hsvaToHex(color);
    } else if (colorType === "2") {
      newColorValue = tinycolor(color).toHslString();
    } else if (colorType === "3") {
      newColorValue = hsvaToRgbString(color);
    }
    setColorValue(newColorValue);
  }, [colorType, color]);

  const isIOSDevice = () => {
    return (
      /iPad|iPhone|iPod/.test(navigator.platform) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
    );
  };

  const sizeWindow = Math.min(width * 0.5, 300);

  const handleColorChange = (newColor) => {
    const { h, s, v, a } = newColor?.hsva ?? {};
    if (h !== undefined && s !== undefined && v !== undefined && a !== undefined) {
      const updatedColor = { h, s, v, a };
      setColor(updatedColor);
    }
  };

  const handleInputChange = () => {
    const inputValue = colorValue;
    let isValid = false;

    switch (colorType) {
      case "1": // Hex
        isValid = tinycolor(inputValue).isValid();
        break;
      case "2": // HSL
        isValid = tinycolor(inputValue).isValid();
        break;
      case "3": // RGB
        isValid = tinycolor(inputValue).isValid();
        break;
    }

    if (isValid) {
      const hsvColor = tinycolor(inputValue).toHsv();
      setColor({
        h: hsvColor.h,
        s: hsvColor.s * 100,
        v: hsvColor.v * 100,
        a: hsvColor.a,
      });
    }
  };

  const handleSave = () => {
    const finalColor = hsvaToHex(color);
    onSave(finalColor);
    onHide();
  };

  const handleClose = () => {
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
    >
      <Modal.Header className="modalTop justify-content-between">
        <h3 className="mb-3 titleModal flex-wrap fw-semibold">
          Kleur aanpassen
        </h3>
        <Button variant="transparent" onClick={handleClose}>
          <FontAwesomeIcon icon={faXmark} className="closeModal" />
        </Button>
      </Modal.Header>
      <Modal.Body className="modalCenter px-4">
        <div className="modal-body modalColorBackdrop py-0">
          <div className="whiteBox sizeWhiteBox py-0">
            <div className="d-flex flex-row justify-content-evenly">
              <div className="me-3 mb-sm-3">
                <Wheel
                  className="d-flex colorwheel"
                  color={color}
                  width={sizeWindow}
                  height={sizeWindow}
                  onChange={handleColorChange}
                />
              </div>
              <div className="d-flex flex-column me-2 align-self-center">
                <div className="d-flex flex-column colorInfo flex-wrap mb-3">
                  <div className="mb-2 w-100">
                    <BorderedDropDown
                      label={"Kleurmodel"}
                      placeholder="Kleurmodel"
                      options={[
                        { title: "HEX", value: "1", key: "1"},
                        { title: "HSL", value: "2", key: "2"},
                        { title: "RGB", value: "3", key: "3"},
                      ]}
                      selectedValue={colorType}
                      setSelectedValue={setColorType}
                    />  
                  </div>
                  <div className="d-flex flex-column mb-2">
                    <BorderedTextInput
                      name="colorInput"
                      label={"Kleurcode"}
                      inputValue={colorValue || ""}
                      setInputValue={setColorValue}
                      updateFunction={handleInputChange}
                    />
                    <small className="smallText fw-semibold mt-2">
                      {colorType === "1" && "Voorbeeld: #ffffff"}
                      {colorType === "2" && "Voorbeeld: hsl(0, 0%, 100%)"}
                      {colorType === "3" && "Voorbeeld: rgb(255, 255, 255)"}
                    </small>
                  </div>
                  <div className="mb-2 w-100">
                    <div 
                      style={{ 
                        width: '100%', 
                        height: '50px', 
                        backgroundColor: hsvaToHex(color),
                        border: '1px solid #ccc',
                        borderRadius: '8px'
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="modalBottom d-flex justify-content-end">
        <ButtonBlue onClickFunction={handleSave} text={"Kleur opslaan"} />  
      </Modal.Footer>
    </Modal>
  );
};

export default ColorPickerModal;